/**
 * Sync service for managing online/offline synchronization
 * Handles syncing local changes to Firestore and vice versa
 */

import { offlineStorage } from './offlineStorage';
import * as tripService from './tripService';

/**
 * Prepare a trip object for IndexedDB storage by converting Timestamp functions to serializable data
 */
const prepareForStorage = (trip) => {
  const prepared = { ...trip };

  // Convert timestamps to serializable format
  if (prepared.createdAt && typeof prepared.createdAt.toMillis === 'function') {
    const millis = prepared.createdAt.toMillis();
    prepared.createdAt = {
      _seconds: Math.floor(millis / 1000),
      _nanoseconds: (millis % 1000) * 1000000,
      _timestamp: millis
    };
  }

  if (prepared.updatedAt && typeof prepared.updatedAt.toMillis === 'function') {
    const millis = prepared.updatedAt.toMillis();
    prepared.updatedAt = {
      _seconds: Math.floor(millis / 1000),
      _nanoseconds: (millis % 1000) * 1000000,
      _timestamp: millis
    };
  }

  return prepared;
};

class SyncService {
  constructor() {
    this.isSyncing = false;
    this.syncListeners = new Set();
    this.networkListeners = new Set();
    this.isOnline = navigator.onLine;
    this.lastSyncTime = null;

    // Listen for online/offline events
    window.addEventListener('online', () => this.handleOnline());
    window.addEventListener('offline', () => this.handleOffline());

    // Suppress certain errors when offline
    this.setupErrorSuppression();
  }

  /**
   * Setup console error suppression for offline mode
   */
  setupErrorSuppression() {
    const originalError = console.error;
    const self = this;

    console.error = function(...args) {
      // If offline, suppress known network errors
      if (!self.isOnline) {
        const errorString = args.join(' ').toLowerCase();
        const suppressPatterns = [
          'network',
          'failed to fetch',
          'firebaseerror',
          'evaluation error',
          'permission-denied',
          'net::err',
          'load failed',
          'cors',
          'beacon.krxd.net',
          'krxd.net'
        ];

        // Check if error matches any suppression pattern
        const shouldSuppress = suppressPatterns.some(pattern =>
          errorString.includes(pattern)
        );

        if (shouldSuppress) {
          // Silently ignore - we know we're offline
          return;
        }
      }

      // Otherwise, log normally
      originalError.apply(console, args);
    };
  }

  /**
   * Check if device is online
   */
  checkOnline() {
    return navigator.onLine;
  }

  /**
   * Handle online event
   */
  async handleOnline() {
    console.log('Network: Online');
    this.isOnline = true;
    this.notifyNetworkListeners(true);

    // Start syncing pending changes
    await this.syncPendingChanges();
  }

  /**
   * Handle offline event
   */
  handleOffline() {
    console.log('Network: Offline');
    this.isOnline = false;
    this.notifyNetworkListeners(false);
  }

  /**
   * Subscribe to sync status changes
   */
  onSyncStatusChange(callback) {
    this.syncListeners.add(callback);
    return () => this.syncListeners.delete(callback);
  }

  /**
   * Subscribe to network status changes
   */
  onNetworkStatusChange(callback) {
    this.networkListeners.add(callback);
    // Immediately call with current status
    callback(this.isOnline);
    return () => this.networkListeners.delete(callback);
  }

  /**
   * Notify sync listeners
   */
  notifySyncListeners(status) {
    this.syncListeners.forEach(callback => {
      try {
        callback(status);
      } catch (error) {
        console.error('Error in sync listener:', error);
      }
    });
  }

  /**
   * Notify network listeners
   */
  notifyNetworkListeners(isOnline) {
    this.networkListeners.forEach(callback => {
      try {
        callback(isOnline);
      } catch (error) {
        console.error('Error in network listener:', error);
      }
    });
  }

  /**
   * Sync a trip from Firestore to local storage
   */
  async syncTripToLocal(tripId, userId) {
    try {
      if (!this.isOnline) {
        console.log('Cannot sync - offline');
        return { success: false, error: 'Device is offline' };
      }

      const result = await tripService.getTrip(tripId, userId);

      if (result.success && result.data) {
        await offlineStorage.saveTrip(prepareForStorage(result.data));
        console.log(`Trip ${tripId} synced to local storage`);
        return { success: true, data: result.data };
      } else {
        console.error(`Failed to sync trip ${tripId}:`, result.error);
        return { success: false, error: result.error };
      }
    } catch (error) {
      console.error('Error syncing trip to local:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Sync all trips for a user from Firestore to local storage
   */
  async syncAllTripsToLocal(userId) {
    try {
      if (!this.isOnline) {
        console.log('Cannot sync - offline');
        return { success: false, error: 'Device is offline' };
      }

      this.notifySyncListeners({ syncing: true, progress: 0 });

      const result = await tripService.getUserTrips(userId);

      if (result.success && result.data) {
        const trips = result.data;
        let synced = 0;

        for (const trip of trips) {
          await offlineStorage.saveTrip(prepareForStorage(trip));
          synced++;
          this.notifySyncListeners({
            syncing: true,
            progress: (synced / trips.length) * 100
          });
        }

        await offlineStorage.setSyncState('lastFullSync', Date.now());
        this.lastSyncTime = Date.now();

        console.log(`Synced ${synced} trips to local storage`);
        this.notifySyncListeners({ syncing: false, progress: 100 });

        return { success: true, count: synced };
      } else {
        this.notifySyncListeners({ syncing: false, error: result.error });
        return { success: false, error: result.error };
      }
    } catch (error) {
      console.error('Error syncing all trips to local:', error);
      this.notifySyncListeners({ syncing: false, error: error.message });
      return { success: false, error: error.message };
    }
  }

  /**
   * Sync pending changes from local storage to Firestore
   */
  async syncPendingChanges() {
    if (this.isSyncing) {
      console.log('Sync already in progress');
      return { success: false, error: 'Sync already in progress' };
    }

    if (!this.isOnline) {
      console.log('Cannot sync - offline');
      return { success: false, error: 'Device is offline' };
    }

    try {
      this.isSyncing = true;
      this.notifySyncListeners({ syncing: true, type: 'upload' });

      const pendingChanges = await offlineStorage.getPendingChanges();

      if (pendingChanges.length === 0) {
        console.log('No pending changes to sync');
        this.isSyncing = false;
        this.notifySyncListeners({ syncing: false });
        return { success: true, count: 0 };
      }

      console.log(`Syncing ${pendingChanges.length} pending changes...`);
      let synced = 0;
      let errors = [];

      for (const change of pendingChanges) {
        try {
          await this.applyChange(change);
          await offlineStorage.markChangeSynced(change.id);
          synced++;
        } catch (error) {
          console.error(`Failed to apply change ${change.id}:`, error);
          errors.push({ changeId: change.id, error: error.message });
        }
      }

      // Clean up synced changes
      await offlineStorage.clearSyncedChanges();

      this.isSyncing = false;
      this.notifySyncListeners({ syncing: false });

      console.log(`Synced ${synced}/${pendingChanges.length} changes`);

      return {
        success: true,
        synced,
        total: pendingChanges.length,
        errors: errors.length > 0 ? errors : undefined
      };
    } catch (error) {
      console.error('Error syncing pending changes:', error);
      this.isSyncing = false;
      this.notifySyncListeners({ syncing: false, error: error.message });
      return { success: false, error: error.message };
    }
  }

  /**
   * Apply a single change to Firestore
   */
  async applyChange(change) {
    const { operation, tripId, userId, data } = change;

    switch (operation) {
      case 'createTrip':
        return await tripService.createTrip(userId, data);

      case 'updateTrip':
        return await tripService.updateTrip(tripId, userId, data);

      case 'deleteTrip':
        return await tripService.deleteTrip(tripId, userId);

      case 'addDay':
        return await tripService.addDayToTrip(tripId, userId, data);

      case 'updateDay':
        return await tripService.updateDayInTrip(tripId, userId, data.dayId, data.updates);

      case 'removeDay':
        return await tripService.removeDayFromTrip(tripId, userId, data.dayId);

      case 'addTripTodo':
        return await tripService.addTodoToTrip(tripId, userId, data.text);

      case 'addDayTodo':
        return await tripService.addTodoToDay(tripId, userId, data.dayId, data.text);

      case 'updateTodo':
        return await tripService.updateTodoInTrip(tripId, userId, data.todoId, data.updates);

      case 'updateDayTodo':
        return await tripService.updateTodoInDay(
          tripId,
          userId,
          data.dayId,
          data.todoId,
          data.updates
        );

      case 'deleteTodo':
        return await tripService.deleteTodoFromTrip(tripId, userId, data.todoId);

      case 'deleteDayTodo':
        return await tripService.deleteTodoFromDay(tripId, userId, data.dayId, data.todoId);

      default:
        throw new Error(`Unknown operation: ${operation}`);
    }
  }

  /**
   * Get last sync time
   */
  async getLastSyncTime() {
    if (this.lastSyncTime) {
      return this.lastSyncTime;
    }

    const lastSync = await offlineStorage.getSyncState('lastFullSync');
    this.lastSyncTime = lastSync;
    return lastSync;
  }

  /**
   * Force a full sync (download all trips from Firestore)
   */
  async forceFullSync(userId) {
    console.log('Starting full sync...');

    // First sync pending changes
    await this.syncPendingChanges();

    // Then download all trips
    return await this.syncAllTripsToLocal(userId);
  }

  /**
   * Get sync statistics
   */
  async getSyncStats() {
    const pendingChanges = await offlineStorage.getPendingChanges();
    const lastSync = await this.getLastSyncTime();
    const storageStats = await offlineStorage.getStats();

    return {
      isOnline: this.isOnline,
      isSyncing: this.isSyncing,
      pendingChanges: pendingChanges.length,
      lastSyncTime: lastSync,
      localTrips: storageStats.trips,
      totalPendingChanges: storageStats.pendingChanges
    };
  }

  /**
   * Clear all offline data
   */
  async clearOfflineData() {
    await offlineStorage.clearAll();
    this.lastSyncTime = null;
    console.log('Offline data cleared');
  }
}

// Export singleton instance
export const syncService = new SyncService();

// Export class for testing
export { SyncService };
